import React, { Fragment, memo, Suspense, useCallback, useMemo, useReducer, useRef, useState, } from 'react';
import { ClassNames } from '@emotion/core';
import styled from '@emotion/styled';
import { KEY_FIRST_THUMBNAIL_RENDERED, KEY_FIRST_VIDEOS_RECEIVED, } from '@assets/utils/fwnPerformance/constants';
import { Enum_LiveStream_Status } from '@core/api';
import { useIntersectionObserver } from '@core/hooks/useIntersectionObserver';
import ThumbnailPicture from '@embed/components/common/ThumbnailPicture';
import { onNavigateToVideoHelper } from '@embed/components/VideoPlayer/onNavigateToVideoHelper';
import { PlayTriggersEnum } from '@embed/constants';
import { customEmbedEvent, EventType } from '@embed/customEmbedEvent';
import { isFalsy } from '@embed/helpers';
import { useTranslation } from '@embed/hooks/react-i18nextLazy';
import { useFirstPartyLiveStream } from '@embed/hooks/useFirstPartyLiveStream';
import { useLiveStreamingLite } from '@embed/hooks/useLiveStreamingLite';
import { getVideoBlurhash, getVideoPoster } from '@src/helpers';
import { ThumbnailBody } from './ThumbnailBody';
import ThumbnailWrapper from './ThumbnailWrapper';
const Autoplay = React.lazy(() => import('./Autoplay'));
const Container = styled('div') `
  background-size: 400% 400%;
  background: linear-gradient(-90deg, #eee, #ccc);
  box-sizing: border-box;
  height: 100%;
  width: 100%;
`;
const ThumbnailContainer = styled('div') `
  background-image: none;
  background-position: center !important;
  background-size: cover !important;
  background-repeat: no-repeat !important;
  border: none !important;
  box-sizing: border-box;
  color: currentColor;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-end;
  opacity: 1;
  position: relative;
  text-align: initial;
  text-decoration: none;
  width: 100%;
  z-index: 1;

  &:hover {
    color: inherit;
    filter: contrast(115%);
    text-decoration: none;
    z-index: 1;
  }
`;
const reducer = (state, action) => {
    let { impressionTimeout, isInViewport, wasInViewport, trackFeedImpression } = state;
    const { index, total, video, actions: { processThumbnailImpressionQueue_throttled, registerInViewport, registerForAutoplay, registerThumbnailImpression, unregisterInViewport, unregisterForAutoplay, }, } = state;
    const { entries, fetchMoreVideos } = action;
    const [{ intersectionRatio }] = entries;
    const initial = {
        impressionTimeout,
        isInViewport,
        wasInViewport,
    };
    // When last (3) thumbnail is reached, call for more videos
    if (total - 3 === index && intersectionRatio > 0.5 && !wasInViewport) {
        fetchMoreVideos && fetchMoreVideos();
    }
    // Register thumbnail in viewport
    if (intersectionRatio > 0.5) {
        if (!isInViewport) {
            trackFeedImpression();
            registerInViewport({ video });
            isInViewport = true;
            wasInViewport = true;
            if (!impressionTimeout) {
                impressionTimeout = setTimeout(() => {
                    // Ask for video to be autoplayed. Using 1s delay to
                    // avoid multiple autoplays while rendering thumbnails
                    registerForAutoplay();
                    // Register thumbnail impression if thumbnail
                    // stays in viewport for at least 1s
                    registerThumbnailImpression({ video });
                    // Call for processing impression queue
                    processThumbnailImpressionQueue_throttled();
                }, 1000);
            }
        }
    }
    else {
        if (isInViewport) {
            unregisterInViewport({ video });
            unregisterForAutoplay({ video });
            isInViewport = false;
            if (impressionTimeout) {
                impressionTimeout = clearTimeout(impressionTimeout);
            }
        }
    }
    const changes = {
        impressionTimeout,
        isInViewport,
        wasInViewport,
    };
    // Dummy compare of two objects to optimize rerenders
    if (JSON.stringify(initial) !== JSON.stringify(changes)) {
        return Object.assign(Object.assign({}, state), changes);
    }
    return state;
};
const memoShouldMemoize = (prev, next) => !(prev.hasAutoplay !== next.hasAutoplay ||
    prev.hasPlayingInPlayerOverlay !== next.hasPlayingInPlayerOverlay ||
    prev.isLarge !== next.isLarge);
export default memo((props) => {
    const { appContext, hasAutoplay, hasPlayingInPlayerOverlay, videoPlayTrigger, index, total, 
    // video,
    params, actions, fetchMoreVideos, isLarge, publisherClient, trackFeedImpression, } = props;
    const { t } = useTranslation();
    // Update video props based on websocket updates if its a livestream.
    const { video } = useLiveStreamingLite({ video: props.video, params });
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const autoplayTimer = useRef(null);
    const isTouched = useRef(false);
    const linkRef = useRef(null);
    const shouldAutoplay = hasAutoplay || isVideoLoaded;
    const [{ isInViewport }, dispatchIntersectionChanged] = useReducer(reducer, {
        index,
        total,
        impressionTimeout: undefined,
        isInViewport: false,
        wasInViewport: false,
        video,
        actions,
        trackFeedImpression,
    });
    useIntersectionObserver({
        callback: useCallback((entries) => dispatchIntersectionChanged({ entries, fetchMoreVideos }), [fetchMoreVideos]),
        element: linkRef.current,
        options: useMemo(() => ({ threshold: [0.5] }), []),
    });
    const { isFirstPartyLiveStream, openInFirstParty } = useFirstPartyLiveStream({
        appContext,
        params,
        video,
    });
    const onClick = useCallback((event) => {
        event === null || event === void 0 ? void 0 : event.preventDefault();
        actions.trackVideoPick({ video });
        if (!isFirstPartyLiveStream) {
            openInFirstParty();
            return;
        }
        if (onNavigateToVideoHelper({ params, appContext, video })) {
            actions.showVideoPlayer({ video, muted: false });
        }
    }, [
        video,
        params,
        actions,
        appContext,
        isFirstPartyLiveStream,
        openInFirstParty,
    ]);
    const handleMouseOver = useCallback(() => {
        if (!isTouched.current) {
            // ensure this is in viewport and not playing
            if (isInViewport && !autoplayTimer.current) {
                // ensure mouse is inside with some delay
                autoplayTimer.current = setTimeout(() => {
                    autoplayTimer.current = null;
                    // start autoplay
                    actions.setPlayTrigger({
                        video,
                        type: PlayTriggersEnum.PLAY_TRIGGER_HOVERPLAY,
                    });
                    actions.setAutoplay({ video });
                }, 500);
            }
        }
    }, [actions, isInViewport, video]);
    const handleMouseLeave = useCallback(() => {
        // cancel mouse enter timeout if any
        if (autoplayTimer.current) {
            clearTimeout(autoplayTimer.current);
            autoplayTimer.current = null;
        }
    }, []);
    const handleMouseEnter = handleMouseOver;
    return (<ClassNames>
      {({ css, cx }) => {
            var _a, _b;
            return (<ThumbnailWrapper className={`cy-thumbnail scrollTo_${video.encoded_id}`} isLarge={isLarge} mode={params.mode}>
          <Container className={cx({ ['sp']: !!video.vast_tag }, { ['autoplay']: hasAutoplay })} onTouchStart={() => {
                    isTouched.current = true;
                }} onMouseEnter={handleMouseEnter} onMouseOver={handleMouseOver} onMouseLeave={handleMouseLeave}>
            <ThumbnailContainer className="cy-thumbnail-link" ref={linkRef} onClick={onClick}>
              <ThumbnailBody video={video} hidePlayButton={isVideoPlaying} hideCaption={isFalsy(params.captions) || isVideoPlaying} delayCaption={2000} isLarge={isLarge} isInViewport={isInViewport}>
                {hasPlayingInPlayerOverlay && (<div css={css `
                      align-items: center;
                      backdrop-filter: blur(10px);
                      background: rgba(0, 0, 0, 0.5);
                      color: rgba(255, 255, 255, 0.5);
                      display: flex;
                      top: 0;
                      right: 0;
                      bottom: 0;
                      left: 0;
                      justify-content: center;
                      position: absolute;
                      text-align: center;
                      z-index: 3;
                    `}>
                    {t('Playing in picture in picture.')}
                  </div>)}
                {video.live_stream_status === Enum_LiveStream_Status.PAUSED && (<div css={css `
                      align-items: center;
                      backdrop-filter: blur(10px);
                      background: rgba(0, 0, 0, 0.5);
                      color: rgba(255, 255, 255, 0.5);
                      display: flex;
                      top: 0;
                      right: 0;
                      bottom: 0;
                      left: 0;
                      justify-content: center;
                      position: absolute;
                      text-align: center;
                      z-index: 1;
                    `}>
                    {t('Host will be right back.')}
                  </div>)}
                <ThumbnailPicture alt={(video === null || video === void 0 ? void 0 : video.caption) || t('Firework video')} css={css `
                    height: 100% !important;
                    margin: 0 !important;
                    object-fit: cover !important;
                    position: absolute !important;
                    top: 0 !important;
                    left: 0 !important;
                    width: 100% !important;
                    -webkit-transform: translate3d(0, 0, 0);
                  `} onLoad={() => {
                    var _a;
                    actions.setThumbnailLoaded({ video });
                    if (index === 0) {
                        customEmbedEvent({
                            type: EventType.FW_EMBEDFEED_THUMBNAILLOAD,
                            params,
                            data: {
                                video,
                            },
                        });
                        (_a = appContext.performance) === null || _a === void 0 ? void 0 : _a.measure(KEY_FIRST_THUMBNAIL_RENDERED, KEY_FIRST_VIDEOS_RECEIVED, undefined);
                    }
                }} loading={index <= 3 ? 'eager' : 'lazy'} src={(_a = getVideoPoster(video)) !== null && _a !== void 0 ? _a : ''} blurhash={getVideoBlurhash(video)}/>
                {shouldAutoplay && !params.a11y_disable_previews && (<Suspense fallback={<Fragment />}>
                    <Autoplay appContext={appContext} publisherClientId={publisherClient === null || publisherClient === void 0 ? void 0 : publisherClient.id} videoId={video.encoded_id} video={video} variant={(_b = video.variant) !== null && _b !== void 0 ? _b : ''} index={index} hasAutoplay={hasAutoplay} videoPlayTrigger={videoPlayTrigger} onVideoPlay={() => setIsVideoPlaying(true)} onVideoPause={() => setIsVideoPlaying(false)} onVideoLoaded={() => setIsVideoLoaded(true)} params={params} actions={actions}/>
                  </Suspense>)}
              </ThumbnailBody>
            </ThumbnailContainer>
          </Container>
        </ThumbnailWrapper>);
        }}
    </ClassNames>);
}, memoShouldMemoize);
